import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, HostListener, Inject, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { combineLatest, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, filter, finalize, switchMap, take, takeUntil } from 'rxjs/operators';
import { fadeAnimation, showAnimation } from 'src/animations';
import { modules } from 'src/app/globals';
import { MAIL_PROVIDER_SERVICE, MSG_PROVIDER_SERVICE } from 'src/app/lib/mail/interfaces';
import { SidenavOption } from 'src/app/lib/shared/classes';
import { ChangeCollisionDialogComponent, ChangeCollisionDialogComponentInputData } from 'src/app/lib/shared/components/dialog/change-collision-dialog/change-collision-dialog.component';
import { CompleteRequestDialogComponent, CompleteRequestDialogComponentInputData } from 'src/app/lib/shared/components/dialog/complete-request-dialog/complete-request-dialog.component';
import { DoubleCheckDialogComponent, DoubleCheckDialogComponentInputData } from 'src/app/lib/shared/components/dialog/double-check-dialog/double-check-dialog.component';
import { ExternalChangePreviewComponent, ExternalChangePreviewComponentData } from 'src/app/lib/shared/components/preview/external-change-preview/external-change-preview.component';
import { UserPipe } from 'src/app/lib/shared/pipes/user.pipe';
import { SidenavService } from 'src/app/lib/shared/services/sidenav.service';
import { WebsocketService } from 'src/app/services/websocket.service';
import { Issue, IssueItem, IssueTransaction } from '../../../classes';
import { IssueTransactionStatus } from '../../../globals';
import { ISSUE_PROVIDER_SERVICE, ISSUE_TRANSACTION_PROVIDER_SERVICE, ISSUE_TYPE_PROVIDER_SERVICE } from '../../../interfaces';
import { IssueTypeNamePipe } from '../../../../shared/pipes/issue-type-name.pipe';
import { IssueTransactionService } from '../../../services/issue-transaction.service';
import { IssueService } from '../../../services/issue.service';
import { IssueHistoryDialogComponent } from '../../dialog/issue-history-dialog/issue-history-dialog.component';
import { IssueTransactionDialog2Component } from '../../dialog/issue-transaction-dialog2/issue-transaction-dialog2.component';
import { IssueDetailViewComponentService } from './issue-detail-view-component.service';
import { FileService } from 'src/app/lib/nextcloud/services/file.service';
import { ItemAssistDialogOutput, ItemAssistentDialogComponent } from '../../dialog/item-assistent-dialog/item-assistent-dialog.component';
import { CONCURRENCY_PROVIDER_SERVICE, OFFERS_PROVIDER_SERVICE } from 'src/app/lib/shared/interfaces';
import { IssueChangeCollisionWrapperComponent } from '../../embedded/issue-change-collision-wrapper/issue-change-collision-wrapper.component';
import { InputDialogComponent, InputDialogComponentInput } from 'src/app/lib/shared/components/dialog/input-dialog/input-dialog.component';
import { SelectFileDialogComponent, SelectFileDialogComponentInputData } from 'src/app/lib/nextcloud/components/dialog/select-file-dialog/select-file-dialog.component';
import { NextcloudFile } from 'src/app/lib/nextcloud/classes';
import { FileViewModes } from 'src/app/lib/nextcloud/components/dialog/select-file-view-dialog/select-file-view-dialog.component';
import { RootService } from 'src/app/services/root.service';
import { IssueTypes } from 'src/app/lib/shared/globals';
import { QuillNoteTypeMismatchDialogComponent } from 'src/app/lib/shared/components/dialog/quill-note-type-mismatch-dialog/quill-note-type-mismatch-dialog.component';
import { SessionService } from 'src/app/lib/shared/services/session.service';
import { CreateIssueDialogComponent, CreateIssueDialogComponentInputData } from '../../dialog/create-issue-dialog/create-issue-dialog.component';
import * as moment from 'moment';
import { SendMailDialogComponent } from '../../dialog/send-mail-dialog/send-mail-dialog.component';
import { CreateOfferComponent, CreateOfferComponentInputData } from 'src/app/lib/offer/components/dialog/create-offer/create-offer.component';
import { DefaultNotification } from 'src/app/lib/default-notification/classes';
import { Offer } from 'src/app/lib/offer/classes';

@Component({
  selector: 'app-issue-detail-view',
  templateUrl: './issue-detail-view.component.html',
  styleUrls: ['./issue-detail-view.component.css'],
  providers: [
    { provide: ISSUE_PROVIDER_SERVICE, useClass: IssueDetailViewComponentService },
    { provide: ISSUE_TYPE_PROVIDER_SERVICE, useExisting: ISSUE_PROVIDER_SERVICE },
    { provide: ISSUE_TRANSACTION_PROVIDER_SERVICE, useExisting: ISSUE_PROVIDER_SERVICE },
    { provide: OFFERS_PROVIDER_SERVICE, useExisting: ISSUE_PROVIDER_SERVICE },
    { provide: MAIL_PROVIDER_SERVICE, useExisting: ISSUE_PROVIDER_SERVICE },
    { provide: MSG_PROVIDER_SERVICE, useExisting: ISSUE_PROVIDER_SERVICE },
    { provide: CONCURRENCY_PROVIDER_SERVICE, useExisting: ISSUE_PROVIDER_SERVICE },
    SidenavService,
    IssueTypeNamePipe,
    UserPipe
  ],
  animations: [showAnimation, fadeAnimation, trigger('expand', [
    state('expanded', style({
      width: '250px'
    })),
    state('closed', style({
      width: '40px'
    })),
    transition('expanded => closed', [
      animate('.1s')
    ]),
    transition('closed => expanded', [
      animate('.1s')
    ]),
  ])
  ]
})
export class IssueDetailViewComponent implements OnDestroy, OnInit {

  modules = modules;

  transactions: IssueTransaction[] = [];

  sidenavExpanded: boolean = true;
  reset: boolean = false;
  showMsgCollection: boolean = false;

  private _sidenavOptions = {
    'editMode': {
      'icon': () => this.disabled ? 'edit' : 'edit_off',
      'title': () => this.disabled ? 'Bearbeiten' : 'Sperren',
      'onClick': () => of(this.toggleEditMode()),
      'visible': () => true,
      'appearance': () => 'default'
    } as SidenavOption,
    'save': {
      'icon': () => this.formGroup.dirty ? 'save' : 'done',
      'title': () => this.formGroup.dirty ? 'Speichern' : 'Aktuell',
      'onClick': () => this.formGroup.dirty ? this.saveChanges() : of(null),
      'visible': () => !this.disabled,
      'appearance': () => this.formGroup.dirty ? 'success' : 'ok'
    } as SidenavOption,
    'discard': {
      'icon': () => 'clear',
      'title': () => 'Verwerfen',
      'onClick': () => this.discardChanges(),
      'visible': () => !this.disabled && this.formGroup.dirty,
      'appearance': () => 'danger'
    } as SidenavOption,
    'history': {
      'icon': () => 'history',
      'title': () => 'Verlauf',
      'onClick': () => this.showIssueHistory(),
      'visible': () => true,
      'appearance': () => 'primary'
    } as SidenavOption,
    'transaction': {
      'icon': () => 'play_arrow',
      'title': () => 'Transaktion',
      'onClick': () => this.showSelectTransaction(),
      'visible': () => !this.disabled && this.stateControl.value,
      'appearance': () => 'primary'
    } as SidenavOption,
    'contact': {
      'icon': () => 'alternate_email',
      'title': () => 'Kontakt',
      'onClick': () => this.sendMail(),
      'visible': () => true,
      'appearance': () => 'primary'
    } as SidenavOption,
    'bookItems': {
      'icon': () => 'input',
      'title': () => 'Artikel buchen',
      'onClick': () => this.showBookItemsDialog(),
      'visible': () => !this.disabled && this.itemsControl.length > 0,
      'appearance': () => 'primary'
    } as SidenavOption,
    'read_more': {
      'icon': () => 'read_more',
      'title': () => 'Zu Vorgang',
      'onClick': () => this.convertToIssue(),
      'visible': () => !this.disabled && this.typeControl.value === IssueTypes.Incident,
      'appearance': () => 'primary',
      'tooltip': 'Erzeugen Sie einen Vorgang aus diesem Ticket'
    } as SidenavOption,
    'archived': {
      'icon': () => this.filesExportedControl.value ? 'done' : 'archive',
      'title': () => this.filesExportedControl.value ? 'Exportiert' : 'Archivieren',
      'onClick': () => this.exportFiles(),
      'visible': () => this.typeControl.value === IssueTypes.Issue,
      'appearance': () => 'primary',
      'tooltip': 'Markiert die Dateien des Vorgang als exportiert'
    } as SidenavOption,
    'watched_status': {
      'icon': () => this.formGroup.controls['isWatched'].value ? 'visibility' : 'visibility_off',
      'title': () => this.formGroup.controls['isWatched'].value ? 'Beobachtet' : 'Nicht beob.',
      'onClick': () => this.toggleWatchedStatus(),
      'visible': () => true,
      'appearance': () => 'dark',
      'tooltip': 'Beobachten Sie den Vorgang um interaktiv über Änderungen informiert zu werden'
    } as SidenavOption,
    'delete': {
      'icon': () => 'delete_forever',
      'title': () => 'Löschen',
      'onClick': () => this.deleteIssue(),
      'visible': () => !this.disabled && this.rightsMap.delete_issues,
      'appearance': () => 'danger'
    } as SidenavOption
  };

  issueTypeHasItems: boolean = false;
  incidentConvertedToIssue: number = null;
  issueCreatedFromIncident: number = null;

  toggleEMailSidenav = false;

  disabled: boolean = false;

  itemsRefreshing: boolean = false;

  rightsMap = {
    "delete_issues": false
  };

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(ISSUE_PROVIDER_SERVICE) public issueDetailViewComponentService: IssueDetailViewComponentService,
    public rootService: RootService,
    private _sidenavService: SidenavService,
    private _formBuilder: FormBuilder,
    private _fileService: FileService,
    private _router: Router,
    private _issueService: IssueService,
    private _issueTransactionSevice: IssueTransactionService,
    private _snackBar: MatSnackBar,
    private _dialog: MatDialog,
    private _websocketService: WebsocketService,
    private _issueTypeNamePipe: IssueTypeNamePipe,
    private _userPipe: UserPipe,
    private _viewContainerRef: ViewContainerRef,
    private _sessionService: SessionService
  ) { }

  addArticle() {
    this.issueDetailViewComponentService.issueId$.pipe(take(1)).subscribe((identifier) => {
      let article = new IssueItem();
      article.itemissue = identifier;
      article.itemposition = Math.max(...(this.formGroup.controls['issueItems'].value as IssueItem[]).map(x => x.itemposition), 0) + 1;
      (this.formGroup.controls['issueItems'] as FormArray).push(this._formBuilder.group(article));
    })
  }

  addFolder() {
    this._dialog.open(InputDialogComponent, {
      panelClass: 'no-padding-dialog-container',
      autoFocus: false,
      data: {
        minLength: 3
      } as InputDialogComponentInput
    }).afterClosed().subscribe((result) => {
      if (result) {
        var location: string = this.folderlocationControl.value;
        if (location[location.length - 1] !== '/') location += '/';
        this._fileService.createNewFolder(`${location}${result}`).subscribe(() => this.reloadFolder());
      }
    })
  }

  addOffer() {
    this.issueDetailViewComponentService.issue$
      .pipe(take(1), switchMap((issue: Issue) => {
        return this._dialog.open(CreateOfferComponent, {
          panelClass: "no-padding-dialog-container",
          autoFocus: false,
          data: {
            patchValue: {
              offertitle: `[Angebot] ${issue.issuetitle}`,
              offerissue: issue.issueid,
              offercustomer: issue.issuecustomer,
              localcontact: issue.localcontactpersonalid,
              commercialcontact: issue.commercialcontactpersonalid,
              offerstate: 1
            },
            preventNavigation: true
          } as CreateOfferComponentInputData
        }).afterClosed();
      })).toPromise();
  }

  afterStoring() {
    this.formGroup.markAsUntouched();
    this.formGroup.markAsPristine();
    this.issueDetailViewComponentService.refreshCurrentIssue();
    this._snackBar.open('Änderungen erfolgreich gespeichert!', 'Speichern', {
      duration: 2000
    });
    this.reset = false;
  }

  clearTitle = () => {
    this.titleControl.setValue(null);
    this.titleControl.markAsDirty();
  }

  convertToIssue(): Observable<Object> {
    this.issueDetailViewComponentService.issue$.pipe(take(1)).subscribe((issue: Issue) => {
      this._dialog.open(CreateIssueDialogComponent, {
        panelClass: 'no-padding-dialog-container',
        data: {
          issue: {
            'issuetitle': issue.issuetitle,
            'issuecustomer': issue.issuecustomer,
            'localcontactpersonalid': issue.localcontactpersonalid,
            'commercialcontactpersonalid': issue.commercialcontactpersonalid,
            'issuedesc': `Vorgang, erzeugt am ${moment().format('DD.MM.YYYY HH')} Uhr aus dem Ticket '${[issue.issuenumber, issue.issuetitle].filter(x => x ? true : false).join(' ')}'.\n\n${issue.issuedesc}`,
            'issuetype': IssueTypes.Issue
          } as Issue,
          afterStoring: (issue: Issue) => {
            return this._issueService.incidentCopiedIntoIssue(this.issueIdControl.value, issue.issueid);
          }
        } as CreateIssueDialogComponentInputData
      });
    });
    return of(null);
  }

  convertNote() {
    this._dialog.open(QuillNoteTypeMismatchDialogComponent, {
      panelClass: 'no-padding-dialog-container',
      data: this.noteControl.value
    }).afterClosed().subscribe((htmlResult: string) => {
      if (htmlResult) {
        this.noteControl.setValue(htmlResult);
        this.noteControl.markAsDirty();
      }
    });
  }

  deleteIssue() {
    let subject = new Subject();
    this.issueDetailViewComponentService.issue$.pipe(take(1))
      .subscribe((issue) => {
        var number = issue && issue.issuenumber ? issue.issuenumber.toString() : (Math.random() * (9000000) + 1000000).toFixed(0);
        this._dialog.open(DoubleCheckDialogComponent, {
          autoFocus: false,
          panelClass: 'no-padding-dialog-container',
          data: {
            mode: 'numeric',
            title: 'Löschen bestätigen',
            text: `Bitte geben Sie die Nummer ${number} ein, um den Vorgang dauerhaft zu löschen`,
            doubleCheckValue: number
          } as DoubleCheckDialogComponentInputData
        }).afterClosed().subscribe((result) => {
          if (result) {
            this.issueDetailViewComponentService.deleteCurrentIssue().subscribe(subject);
          } else subject.complete();
        });
      });
    return subject.asObservable();
  }

  discardChanges(): Observable<Object> {
    this.reset = true;
    return this.issueDetailViewComponentService.refreshCurrentIssue();
  }

  executeTransaction(issueTransaction: IssueTransaction) {
    this.issueDetailViewComponentService.setIssueTransaction(issueTransaction);
    this._dialog.open(IssueTransactionDialog2Component, {
      panelClass: "no-padding-dialog-container",
      autoFocus: false,
      viewContainerRef: this._viewContainerRef
    }).afterClosed().subscribe((status) => {
      if (status !== IssueTransactionStatus.Initial) this._issueService.getIssueById(this.issueIdControl.value, false);
    });
  }

  exportFiles() {
    this.filesExportedControl.setValue(this.filesExportedControl.value ? false : true);
    this.filesExportedControl.markAsDirty();
  }

  initRights = (soft: boolean = true) => {
    for (let index in this.rightsMap) this._sessionService.hasRight(index, soft).subscribe(result => this.rightsMap[index] = result);
  }

  navigateToContact = (identifier: number) => this._router.navigate(['/contact-details', identifier]);
  navigateToCustomer = (identifier: number) => this._router.navigate(['/customer-details', identifier]);

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) {
      sub.unsubscribe();
      this._subscriptions = [];
    }
    this.issueDetailViewComponentService.dispose();
  }

  ngOnInit(): void {

    this._sidenavService.setOptions(Object.values(this._sidenavOptions));

    this._subscriptions.push(...[
      this.issueDetailViewComponentService.issue$.subscribe((issue: Issue) => {
        if(this.formGroup.pristine || this.reset) this.setIssue(issue);
      }),
      this.issueDetailViewComponentService.issueItems$.pipe(filter(_ => this.formGroup.pristine || this.reset)).subscribe((items: IssueItem[]) => {
        this.itemsControl.clear();
        items.forEach(x => {
          let group = this._formBuilder.group(x);
          this.itemsControl.push(group);
          if (this.disabled) group.disable();
        });
        this.itemsRefreshing = false;
      }),
      this._websocketService.issueChanged$.pipe(filter(x => x.identifier === this.issueIdControl.value), switchMap(message => combineLatest([of(message), this._userPipe.transform(message.userId)]))).subscribe({
        next: ([message, userName]) => {
          if (this.formGroup.dirty) {
            this._dialog.open(ChangeCollisionDialogComponent, {
              panelClass: 'no-padding-dialog-container',
              disableClose: true,
              data: {
                externalChange: { message: message.message, user: userName, date: message.date, identifier: message.identifier },
                localState: this.formGroup.controls['issue'].value,
                typeName: this._issueTypeNamePipe.transform(this.typeControl.value),
                inner: IssueChangeCollisionWrapperComponent,
                objectProvider: (identifier) => this._issueService.getIssueById(identifier, false)
              } as ChangeCollisionDialogComponentInputData
            });
          } else this.showExternalChangeNotification(message);
        }
      }),
      this.formGroup.controls['userAssignments'].valueChanges.pipe(debounceTime(100)).subscribe((value) => {
        if (this.formGroup.pristine && value.length > 0) this.formGroup.markAsDirty();
      }),
      combineLatest([this.formGroup.controls['issueItems'].valueChanges, this.issueDetailViewComponentService.issue$, this.issueDetailViewComponentService.issueItems$]).pipe(debounceTime(100)).subscribe(([value, issue, items]) => {
        if (this.formGroup.pristine && ((Object.values((this.formGroup.controls['issueItems'] as FormArray).controls).some(x => x.dirty)) || this.formGroup.controls['issueItems'].value.length !== items.length)) this.formGroup.markAsDirty();
      }),
      this.noteControl.valueChanges.pipe(take(1), takeUntil(timer(2000))).subscribe((note: string) => {
        if (note && note.startsWith('{"ops":[{')) this.convertNote();
      })
    ]);

    this.initRights();
  }

  offerClicked = (offer: Offer) => this._router.navigate(['edit-offer', offer.offerid]);
  openItemAssist = () => {
    this._dialog.open(ItemAssistentDialogComponent, {
      panelClass: 'no-padding-dialog-container'
    }).afterClosed().subscribe((result: ItemAssistDialogOutput) => {
      let max = Math.max(...this.itemsControl.value.map(x => x.itemposition), 0);
      for (let item of result.items) {
        max++;
        this.itemsControl.push(this._formBuilder.group({
          itemid: null,
          itembuildindate: item.itembuildindate,
          itemcanceldate: item.itemcanceldate,
          itemcount: 1,
          itemdesc: item.itemdesc,
          itemfaktdate: item.itemfaktdate,
          iteminfo: null,
          itemposition: max,
          itemproduct: item.itemproduct,
          itempurchasecost: item.itempurchasecost,
          itemrentalcost: item.itemrentalcost,
          itemsetupcost: item.itemsetupcost,
          itemcontract: null,
          itemissue: this.issueIdControl.value,
          itemname: item.itemservice,
          issueitemcontractitemtransformation: null
        }));
      }
    });
  };
  openNextcloud = () => window.open(`https://cloud.campus.team/index.php/apps/files/?dir=${this.folderlocationControl.value}`);

  reloadFolder = () => {
    let value = this.folderlocationControl.value;
    let pristine = this.folderlocationControl.pristine;
    setTimeout(() => {
      this.folderlocationControl.setValue(null);
      this.folderlocationControl.setValue(value);
      if (pristine) this.folderlocationControl.markAsPristine();
    }, 10);
  }

  resetIssueItems() {
    this.itemsRefreshing = true;
    this.issueDetailViewComponentService.refreshCurrentItems();
  }

  saveChanges(): Observable<any> {
    let subject = new Subject();

    combineLatest([
      this._dialog.open(CompleteRequestDialogComponent, {
        panelClass: 'no-padding-dialog-container',
        data: {
          addNote: true
        } as CompleteRequestDialogComponentInputData
      }).afterClosed(),
      this.issueDetailViewComponentService.issue$,
      this.issueDetailViewComponentService.issueItems$
    ])
      .pipe(take(1))
      .subscribe(([output, issue, items]) => {

        if (output.abort) {
          subject.complete();
          return;
        }

        let formItemIds = (this.formGroup.controls['issueItems'].value as IssueItem[]).map(x => x.itemid);
        this.reset = true;
        this.issueDetailViewComponentService.saveChanges(
          this.formGroup.controls['issue'].value,
          output.note,
          this.formGroup.controls['userAssignments'].value,
          Object.values((this.formGroup.controls['issueItems'] as FormArray).controls).filter(x => x.dirty || x.value.itemid === null).map(x => x.value),
          items.map(x => x.itemid).filter(x => formItemIds.indexOf(x) === -1)
        )
          .pipe(finalize(() => this.afterStoring()))
          .subscribe(subject);
      });

    return subject.asObservable();
  }

  selectNextcloudFolder() {
    this._dialog.open(SelectFileDialogComponent, {
      panelClass: 'no-padding-dialog-container',
      autoFocus: false,
      data: {
        visibleTypes: ['folder'],
        showConfirmSelection: true
      } as SelectFileDialogComponentInputData
    }).afterClosed().subscribe((result: NextcloudFile) => {
      if (result) this.folderlocationControl.setValue(result.href);
    });
  }

  sendMail() {
    this._issueService.getCustomerDefaultNotifications().subscribe((defaultNotifications: DefaultNotification[]) => {
      this.issueDetailViewComponentService.setDefaultNotifications(defaultNotifications);
      this.issueDetailViewComponentService.setIssueTransaction(null);
    });
    this._dialog.open(SendMailDialogComponent, {
      panelClass: 'no-padding-dialog-container',
      viewContainerRef: this._viewContainerRef
    }).afterClosed().subscribe((result) => {
      if (result) this.issueDetailViewComponentService.refreshCurrentMails();
    });
  }

  setIssue(issue: Issue) {
    this.reset = false;
    this.issueTypeHasItems = issue.issuetype === IssueTypes.Issue;
    this.formGroup.controls['issue'].patchValue(issue);
    console.log(this.formGroup.controls['issue'].value);
    (this.formGroup.controls['userAssignments'] as FormArray).clear();
    this.formGroup.markAsPristine();
    this.resetIssueItems();
    this.incidentConvertedToIssue = Array.isArray(issue.followers)? issue.followers[0] : null;
    this.issueCreatedFromIncident = Array.isArray(issue.anchestors)? issue.anchestors[0] : null;
    if (this.incidentConvertedToIssue && !this.disabled) this.toggleEditMode();
    timer(100).pipe(switchMap(() => this._issueService.issueIsWatched(issue.issueid))).subscribe((result: boolean) => this.formGroup.controls['isWatched'].setValue(result));
  }

  setTransactions() {
    if (this.stateControl.value) {
      this._issueService.loadIssueTransactions(this.stateControl.value)
        .subscribe((result: IssueTransaction[]) => this.transactions = result)
    }
  }

  showBookItemsDialog = () => this.issueDetailViewComponentService.showBookItemsDialog(this._viewContainerRef);

  showExternalChangeNotification(message: { date: string, userId: string, identifier: number, message: string }) {
    this._userPipe.transform(message.userId).subscribe((userName: string) => {
      this._snackBar.openFromComponent(ExternalChangePreviewComponent, {
        horizontalPosition: 'right',
        verticalPosition: 'top',
        data: {
          typeName: this._issueTypeNamePipe.transform(this.typeControl.value),
          externalChange: { message: message.message, user: userName, date: message.date }
        } as ExternalChangePreviewComponentData
      });
    });
  }

  showIssue = (issueId: number) => {
    if (this.disabled) this.toggleEditMode();
    this._router.navigate(['/issue-details', issueId]);
  }

  showIssueHistory() {
    this._dialog.open(IssueHistoryDialogComponent, {
      panelClass: ['no-padding-dialog-container', 'full-screen-transparent-dialog-container'],
      data: { service: this.issueDetailViewComponentService }
    });
    return of(null);
  }

  showSelectTransaction() {
    this._issueTransactionSevice.getIssueTransactionByIssueStatus(this.stateControl.value)
      .pipe(take(1))
      .subscribe({
        next: (issueTransactions: IssueTransaction[]) => this._sidenavService.setOneTimeOptions(issueTransactions.map(x => {
          return { 'title': () => x.name, 'onClick': () => of(this.executeTransaction(x)) } as SidenavOption;
        }), false)
      });
    return of(null);
  }

  toggleEditMode() {
    let pristine = this.formGroup.pristine;
    this.formGroup[this.disabled ? 'enable' : 'disable']({ emitEvent: false });
    this.disabled = !this.disabled;
    if (pristine) this.formGroup.markAsPristine();
  }

  toggleWatchedStatus() {
    let value = this.formGroup.controls['isWatched'].value ? true : false;
    this.issueDetailViewComponentService.issueId$.pipe(switchMap((issueId: number) => this._issueService.setIssueWatchedStatus(issueId, !value)))
      .subscribe(() => this.formGroup.controls['isWatched'].setValue(!value));

    return of(null);
  }

  uploadFiles() {
    this.rootService.uploadFiles(this.folderlocationControl.value).subscribe({
      next: () => this.reloadFolder()
    })
  }

  @HostListener('keydown', ['$event'])
  private _keydown(event: KeyboardEvent) {
    if (event.ctrlKey && event.key === 's') {
      event.preventDefault();
      event.stopPropagation();
      if (this.formGroup.dirty) this.saveChanges();
      else this._snackBar.open('Keine Änderungen zum Speichern erkannt', 'Ok', {
        duration: 2000
      });
    }
  }

  FileViewModes = FileViewModes;

  // CONTROLS
  get customerControl(): AbstractControl {
    return (this.formGroup.controls['issue'] as FormGroup).controls['issuecustomer'];
  }
  get commercialContactControl(): AbstractControl {
    return (this.formGroup.controls['issue'] as FormGroup).controls['commercialcontactpersonalid'];
  }
  get issueIdControl(): AbstractControl {
    return (this.formGroup.controls['issue'] as FormGroup).controls['issueid'];
  }
  get itemsControl(): FormArray {
    return this.formGroup.controls['issueItems'] as FormArray;
  }
  get localContactControl(): AbstractControl {
    return (this.formGroup.controls['issue'] as FormGroup).controls['localcontactpersonalid'];
  }
  get folderlocationControl(): AbstractControl {
    return (this.formGroup.controls['issue'] as FormGroup).controls['folderlocation'];
  }
  get formGroup(): FormGroup {
    return this.issueDetailViewComponentService.formGroup;
  }
  get numberControl(): AbstractControl {
    return (this.formGroup.controls['issue'] as FormGroup).controls['issuenumber'];
  }
  get noteControl(): AbstractControl {
    return (this.formGroup.controls['issue'] as FormGroup).controls['issuenote'];
  }
  get offersControl(): FormArray {
    return (this.formGroup.controls['issue'] as FormGroup).controls['offers'] as FormArray;
  }
  get stateControl(): AbstractControl {
    return (this.formGroup.controls['issue'] as FormGroup).controls['issuestate'];
  }
  get titleControl(): AbstractControl {
    return (this.formGroup.controls['issue'] as FormGroup).controls['issuetitle'];
  }
  get typeControl(): AbstractControl {
    return (this.formGroup.controls['issue'] as FormGroup).controls['issuetype'];
  }
  get filesExportedControl(): AbstractControl {
    return (this.formGroup.controls['issue'] as FormGroup).controls['filesexported'];
  }
}
